import { useState, useEffect, useMemo } from 'react'
import api from '../../api/axiosInstance'
import Swal from 'sweetalert2'
import { useSelector } from 'react-redux'
import { selectUser } from '../../redux/features/auth/authSelectors'
import { getUserPlan } from '../../utils/plan'
import {
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  useTheme,
  MenuItem,
  FormControl,
  Select,
  Divider,
  IconButton,
  FormHelperText,
  Alert,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  InputAdornment,
  Chip,
  alpha,
  Card,
} from '@mui/material'
import ModernCronScheduleEditor from '../../components/cronShceduler/cronShceduleSelector'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Timer as TimerIcon,
  Public as PublicIcon,
  Code as CodeIcon,
  DeveloperBoard as DeveloperBoardIcon,
  Add,
  Remove,
} from '@mui/icons-material'
import ComputerIcon from '@mui/icons-material/Computer'
import { useNavigate, useParams } from 'react-router-dom'
import { cookies } from '../../utils/cookie'
import { jwtDecode } from 'jwt-decode'
import { useFormik } from 'formik'
import {
  newHttpMonitorFormSchema,
  newPingMonitorFormSchema,
  newPortMonitorFormSchema,
  newCronJobMonitorFormSchema,
  newKeywordMonitorFormSchema,
} from '../../utils/formSchema/formSchemas'
import { CheckIcon, LockIcon } from 'lucide-react'

const monitorAlertMessages = [
  {
    monitorType: 'http',
    message:
      'HTTPS Monitörü, belirlediğiniz bir web adresine (örneğin https://www.ornek.com) belirli aralıklarla istekte bulunarak sitenin erişilebilirliğini ve düzgün yanıt verip vermediğini kontrol eder. Sunucudan gelen HTTP durum kodunu (200, 404, 500 gibi) ve sayfanın yanıt süresini izler.',
  },
  {
    monitorType: 'ping',
    message: `Ping Monitörü, bir sunucunun çevrimiçi (erişilebilir) olup olmadığını kontrol etmek için ICMP protokolünü kullanarak düzenli aralıklarla sinyal gönderir. Gönderilen bu 'ping' sinyallerine sunucu cevap verirse, sunucunun aktif olduğu anlaşılır.`,
  },
  {
    monitorType: 'port',
    message: `Port Monitörü, belirli bir IP adresinde ya da alan adında tanımladığınız ağ portunun (örneğin 21, 22, 80, 443 gibi) açık ve ulaşılabilir olup olmadığını düzenli olarak kontrol eder.`,
  },
  {
    monitorType: 'keyword',
    message: `Anahtar Kelime Monitörü, belirli bir web sayfasının içeriğinde sizin belirlediğiniz kelimelerin var olup olmadığını düzenli aralıklarla kontrol eder. Belirttiğiniz kelime bulunmazsa ya da istenmeyen bir kelime tespit edilirse sizi uyarır.`,
  },
  {
    monitorType: 'cronjob',
    message: `Cron Job Monitörü, zamanlanmış görevlerin (cron job'ların) düzgün şekilde çalışıp çalışmadığını takip eder. Genellikle sunucu tarafında arka planda belirli aralıklarla çalışan bu görevler, monitöre entegre edilen özel bir URL'ye her çalıştıklarında istek gönderir.`,
  },
]

export default function NewMonitorPage(update = false) {
  const userReducer = useSelector(selectUser)
  const [params, setParams] = useState(useParams())

  // Admin, route'ta userId varken başka bir kullanıcı adına monitör
  // oluşturuyor/düzenliyor demektir. Bu durumda özellik ve limit filtreleri
  // O kullanıcının paketine göre olmalı; giriş yapan admin'in paketine göre
  // DEĞİL. Bu yüzden hedef kullanıcının aboneliğini ayrıca çekiyoruz.
  const isAdminForUser = Boolean(params.userId)
  const [targetUser, setTargetUser] = useState(null)

  useEffect(() => {
    if (!isAdminForUser) return
    let active = true
    api
      .get(`users/${params.userId}`)
      .then((res) => {
        if (active) setTargetUser(res.data)
      })
      .catch((err) =>
        console.error('Hedef kullanıcının paketi alınamadı:', err),
      )
    return () => {
      active = false
    }
  }, [params.userId])

  // Backend camelCase alan adlarıyla dönüyor, bu sayfa snake_case okuyor.
  // Dönüşüm tek noktada: utils/plan. Abonelik yoksa FREE_PLAN döner.
  const plan = useMemo(
    () => (isAdminForUser ? getUserPlan(targetUser) : getUserPlan(userReducer)),
    [isAdminForUser, targetUser, userReducer],
  )
  const [monitorType, setMonitorType] = useState('http')
  const [min, setMin] = useState(Number(plan.limits.min_interval))
  const [max, setMax] = useState(Number(plan.limits.max_interval))

  // Hedef kullanıcının paketi (admin modunda) sonradan yüklendiğinde interval
  // sınırlarını güncelle.
  useEffect(() => {
    setMin(Number(plan.limits.min_interval))
    setMax(Number(plan.limits.max_interval))
  }, [plan])
  const [role, setRole] = useState('')
  const navigate = useNavigate()
  const theme = useTheme()
  const [vaidateOnChangeState, setValidateOnChangeState] = useState(false)
  const [vaidateOnBlurState, setValidateOnBlurState] = useState(true)
  const [isLoading, setIsLoading] = useState(update.update)
  const monitorTypes = [
    {
      id: 'http',
      icon: <PublicIcon sx={{ color: 'white' }} />,
      bgcolor: '#10b981',
      title: 'HTTP(S)',
      info: "Web sitelerini ve web API'lerini izleyin",
      access: plan.features.http_monitoring,
    },
    {
      id: 'ping',
      icon: <ComputerIcon sx={{ color: 'white' }} />,
      bgcolor: '#6366f1',
      title: 'PING',
      info: 'Ağ bağlantısından ICMP protokolünden izleyin',
      access: plan.features.ping_monitoring,
    },
    {
      id: 'port',
      icon: <DeveloperBoardIcon sx={{ color: 'white' }} />,
      bgcolor: '#f59e0b',
      title: 'PORT',
      info: 'Belirli bağlantı portları izleyin',
      access: plan.features.port_monitoring,
    },
    {
      id: 'keyword',
      icon: <CodeIcon sx={{ color: 'white' }} />,
      bgcolor: '#ef4444',
      title: 'KEYWORD',
      info: 'Web servislerdeki belirli anahtar kelimeleri izleyin',
      access: plan.features.keyword_monitoring,
    },
    {
      id: 'cronjob',
      icon: <TimerIcon sx={{ color: 'white' }} />,
      bgcolor: '#8b5cf6',
      title: 'CRON JOB',
      info: 'Tekrarlanan işleri izleyin',
      access: plan.features.cronjob_monitoring,
    },
  ]

  const HTTP_METHODS = [
    { value: 'GET', label: 'GET', access: plan.features.get_request },
    { value: 'POST', label: 'POST', access: plan.features.post_request },
    { value: 'PUT', label: 'PUT', access: plan.features.put_request },
    { value: 'OPTIONS', label: 'OPTIONS', access: plan.features.options_request },
    { value: 'DELETE', label: 'DELETE', access: plan.features.delete_request },
    { value: 'PATCH', label: 'PATCH', access: plan.features.patch_request },
    { value: 'HEAD', label: 'HEAD', access: plan.features.head_request },
  ]

  const getFormikConfig = () => {
    const baseConfig = {
      isInitialValid: false,
      validateOnChange: vaidateOnChangeState,
      validateOnBlur: vaidateOnBlurState,
      onSubmit: update.update ? updateMonitor : createMonitor,
    }

    switch (monitorType) {
      case 'http':
        return {
          ...baseConfig,
          initialValues: {
            name: '',
            host: '',
            method: 'HEAD',
            body: '',
            headers: '',
            allowedStatusCodes: '',
            timeOut: 30,
            cronSchedule: '*/5 * * * *',
            failCountRef: 3,
            slowResponseAlertStatus: false,
            slowResponseAlertValue: 1000,
            sslCertControl: false,
            sslCertExpire: false,
            domainExpire: false,
          },
          validationSchema: newHttpMonitorFormSchema,
        }
      case 'keyword':
        return {
          ...baseConfig,
          initialValues: {
            name: '',
            host: '',
            method: 'GET',
            body: '',
            headers: '',
            keyWord: '',
            keyWordType: 'txt',
            allowedStatusCodes: '',
            timeOut: 30,
            cronSchedule: '*/5 * * * *',
            failCountRef: 3,
            slowResponseAlertStatus: false,
            slowResponseAlertValue: 1000,
            sslCertControl: false,
            sslCertExpire: false,
            domainExpire: false,
          },
          validationSchema: newKeywordMonitorFormSchema,
        }
      case 'ping':
        return {
          ...baseConfig,
          initialValues: {
            name: '',
            host: '',
            cronSchedule: '*/5 * * * *',
            failCountRef: 3,
            slowResponseAlertStatus: false,
            slowResponseAlertValue: 1000,
          },
          validationSchema: newPingMonitorFormSchema,
        }
      case 'port':
        return {
          ...baseConfig,
          initialValues: {
            name: '',
            host: '',
            port: '',
            timeOut: 30,
            cronSchedule: '*/5 * * * *',
            failCountRef: 3,
            slowResponseAlertStatus: false,
            slowResponseAlertValue: 1000,
          },
          validationSchema: newPortMonitorFormSchema,
        }
      case 'cronjob':
        return {
          ...baseConfig,
          initialValues: {
            name: '',
            deviationTime: 0,
            failCountRef: 3,
          },
          validationSchema: newCronJobMonitorFormSchema,
        }
      default:
        return {
          ...baseConfig,
          initialValues: {},
          validationSchema: null,
        }
    }
  }

  const handleIncrementForFailCount = () => {
    setFieldValue('failCountRef', values.failCountRef + 1)
  }

  const handleDecrementForFailCount = () => {
    setFieldValue('failCountRef', values.failCountRef - 1)
  }

  const alertContent = monitorAlertMessages.find(
    (item) => item.monitorType === monitorType,
  )?.message

  useEffect(() => {
    const fetchMonitorData = async () => {
      try {
        const jwtToken = cookies.get('jwt-access')
        if (jwtToken) {
          const decodedToken = jwtDecode(jwtToken)
          setRole(decodedToken.role)
        }
        const response = await api.get(`monitors/update/${params.id}`)
        console.log(response.data)
        switch (response.data.monitorType) {
          case 'HttpMonitor': {
            setMonitorType('http')
            setFieldValue('name', response.data.name)
            setFieldValue('host', response.data.httpMonitor.host)
            setFieldValue('method', response.data.httpMonitor.method)
            setFieldValue('headers', JSON.stringify(response.data.httpMonitor.headers))
            setFieldValue('body', JSON.stringify(response.data.httpMonitor.body))
            setFieldValue(
              'allowedStatusCodes',
              response.data.httpMonitor.allowedStatusCodes
                ? response.data.httpMonitor.allowedStatusCodes.join(',')
                : ''
            )
            setFieldValue('cronSchedule', response.data.cronSchedule)
            setFieldValue('timeOut', response.data.httpMonitor.timeOut)
            setFieldValue('failCountRef', response.data.failCountRef)
            setFieldValue('slowResponseAlertStatus', response.data.httpMonitor.slowResponseAlertStatus)
            setFieldValue('slowResponseAlertValue', response.data.httpMonitor.slowResponseAlertValue)
            setFieldValue('sslCertControl', response.data.httpMonitor.sslCertControl)
            setFieldValue('sslCertExpire', response.data.httpMonitor.sslCertExpire)
            setFieldValue('domainExpire', response.data.httpMonitor.domainExpire)
            break
          }
          case 'PingMonitor': {
            setMonitorType('ping')
            setFieldValue('name', response.data.name)
            setFieldValue('host', response.data.pingMonitor.host)
            setFieldValue('cronSchedule', response.data.cronSchedule)
            setFieldValue('failCountRef', response.data.failCountRef)
            setFieldValue('slowResponseAlertStatus', response.data.pingMonitor.slowResponseAlertStatus)
            setFieldValue('slowResponseAlertValue', response.data.pingMonitor.slowResponseAlertValue)
            break
          }
          case 'PortMonitor': {
            setMonitorType('port')
            setFieldValue('name', response.data.name)
            setFieldValue('host', response.data.portMonitor.host)
            setFieldValue('port', response.data.portMonitor.port)
            setFieldValue('timeOut', response.data.portMonitor.timeOut)
            setFieldValue('cronSchedule', response.data.cronSchedule)
            setFieldValue('failCountRef', response.data.failCountRef)
            setFieldValue('slowResponseAlertStatus', response.data.portMonitor.slowResponseAlertStatus)
            setFieldValue('slowResponseAlertValue', response.data.portMonitor.slowResponseAlertValue)
            break
          }
          case 'KeywordMonitor': {
            setMonitorType('keyword')
            setFieldValue('name', response.data.name)
            setFieldValue('host', response.data.keyWordMonitor.host)
            setFieldValue('method', response.data.keyWordMonitor.method)
            setFieldValue(
              'headers',
              response.data.keyWordMonitor.headers.length > 0
                ? JSON.stringify(response.data.keyWordMonitor.headers)
                : ''
            )
            setFieldValue(
              'body',
              response.data.keyWordMonitor.body.length > 0
                ? JSON.stringify(response.data.keyWordMonitor.body)
                : ''
            )
            setFieldValue('keyWordType', response.data.keyWordMonitor.keyWordType || 'txt')
            setFieldValue('keyWord', response.data.keyWordMonitor.keyWord)
            setFieldValue(
              'allowedStatusCodes',
              response.data.keyWordMonitor.allowedStatusCodes
                ? response.data.keyWordMonitor.allowedStatusCodes.join(',')
                : ''
            )
            setFieldValue('cronSchedule', response.data.cronSchedule)
            setFieldValue('timeOut', response.data.keyWordMonitor.timeOut)
            setFieldValue('failCountRef', response.data.failCountRef)
            setFieldValue('slowResponseAlertStatus', response.data.keyWordMonitor.slowResponseAlertStatus)
            setFieldValue('slowResponseAlertValue', response.data.keyWordMonitor.slowResponseAlertValue)
            setFieldValue('sslCertControl', response.data.keyWordMonitor.sslCertControl ?? false)
            setFieldValue('sslCertExpire', response.data.keyWordMonitor.sslCertExpire ?? false)
            setFieldValue('domainExpire', response.data.keyWordMonitor.domainExpire ?? false)
            break
          }
          case 'CronJobMonitor': {
            setMonitorType('cronjob')
            setFieldValue('name', response.data.name)
            setFieldValue('deviationTime', response.data.cronJobMonitor.deviationTime)
            setFieldValue('cronSchedule', response.data.cronSchedule)
            setFieldValue('failCountRef', response.data.failCountRef)
            break
          }
          default: {
            break
          }
        }
        setIsLoading(false)
      } catch (error) {
        Swal.fire({
          title: 'Hata',
          text: 'Monitor bilgileri alınırken bir hata oluştu.',
          icon: 'error',
          confirmButtonText: 'Tamam',
          confirmButtonColor: '#10b981',
          background: alpha('#0f172a', 0.98),
          color: '#fff',
        })
        turnMonitorPage()
        console.error('Monitor bilgileri alınırken hata oluştu:', error)
      }
    }
    if (update.update) {
      fetchMonitorData()
    } else {
      const jwtToken = cookies.get('jwt-access')
      if (jwtToken) {
        const decodedToken = jwtDecode(jwtToken)
        setRole(decodedToken.role)
      }
    }
  }, [])

  const createMonitor = async (values, actions) => {
    try {
      let url = ''
      let formattedData = {
        name: values.name,
        cronSchedule: values.cronSchedule,
        failCountRef: values.failCountRef,
      }
      switch (monitorType) {
        case 'http': {
          formattedData.httpMonitor = {
            host: values.host,
            method: values.method,
            body: values.body.length > 0 ? JSON.parse(values.body) : {},
            headers: values.headers.length > 0 ? JSON.parse(values.headers) : {},
            allowedStatusCodes:
              values.allowedStatusCodes.length > 0
                ? values.allowedStatusCodes.split(',').map((code) => code.trim())
                : [],
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
            sslCertControl: values.sslCertControl,
            sslCertExpire: values.sslCertExpire,
            domainExpire: values.domainExpire,
          }
          url = role === 'admin' ? `monitors/http/${params.userId}` : `monitors/http/`
          break
        }
        case 'ping': {
          formattedData.pingMonitor = {
            host: values.host,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
          }
          url = role === 'admin' ? `monitors/ping/${params.userId}` : `monitors/ping/`
          break
        }
        case 'port': {
          formattedData.portMonitor = {
            host: values.host,
            port: Number(values.port),
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
          }
          url = role === 'admin' ? `monitors/port/${params.userId}` : `monitors/port/`
          break
        }
        case 'keyword': {
          formattedData.keyWordMonitor = {
            host: values.host,
            method: values.method,
            body: values.body.length > 0 ? JSON.parse(values.body) : {},
            headers: values.headers.length > 0 ? JSON.parse(values.headers) : {},
            keyWordType: values.keyWordType,
            keyWord: values.keyWord,
            allowedStatusCodes:
              values.allowedStatusCodes.length > 0
                ? values.allowedStatusCodes.split(',').map((code) => code.trim())
                : [],
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
            sslCertControl: values.sslCertControl,
            sslCertExpire: values.sslCertExpire,
            domainExpire: values.domainExpire,
          }
          url = role === 'admin' ? `monitors/keyword/${params.userId}` : `monitors/keyword/`
          break
        }
        case 'cronjob': {
          formattedData.cronJobMonitor = {
            deviationTime: Number(values.deviationTime),
          }
          url = role === 'admin' ? `monitors/cronjob/${params.userId}` : `monitors/cronjob/`
          break
        }
        default: {
          break
        }
      }
      console.log(formattedData)
      const response = await api.post(url, formattedData)
      if (response.data) {
        Swal.fire({
          title: 'İzleme Başarılı Şekilde Oluşturuldu',
          icon: 'success',
          confirmButtonText: 'Tamam',
          confirmButtonColor: '#10b981',
          background: alpha('#0f172a', 0.98),
          color: '#fff',
        })
        turnMonitorPage()
      }
    } catch (error) {
      Swal.fire({
        title: error.response.data.message,
        icon: 'error',
        confirmButtonText: 'Tamam',
        confirmButtonColor: '#10b981',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
      })
      console.error('Sunucu eklenirken hata oluştu:', error)
    }
  }

  const updateMonitor = async (e) => {
    try {
      let url = ''
      let formattedData = {
        name: values.name,
        cronSchedule: values.cronSchedule,
        failCountRef: values.failCountRef,
      }
      switch (monitorType) {
        case 'http': {
          formattedData.httpMonitor = {
            host: values.host,
            method: values.method,
            body: values.body.length > 0 ? JSON.parse(values.body) : {},
            headers: values.headers.length > 0 ? JSON.parse(values.headers) : {},
            allowedStatusCodes:
              values.allowedStatusCodes.length > 0
                ? values.allowedStatusCodes.split(',').map((code) => code.trim())
                : [],
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
            sslCertControl: values.sslCertControl,
            sslCertExpire: values.sslCertExpire,
            domainExpire: values.domainExpire,
          }
          url = `monitors/http/${params.id}`
          break
        }
        case 'ping': {
          formattedData.pingMonitor = {
            host: values.host,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
          }
          url = `monitors/ping/${params.id}`
          break
        }
        case 'port': {
          formattedData.portMonitor = {
            host: values.host,
            port: Number(values.port),
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
          }
          url = `monitors/port/${params.id}`
          break
        }
        case 'keyword': {
          formattedData.keyWordMonitor = {
            host: values.host,
            method: values.method,
            body: values.body.length > 0 ? JSON.parse(values.body) : {},
            headers: values.headers.length > 0 ? JSON.parse(values.headers) : {},
            keyWordType: values.keyWordType,
            keyWord: values.keyWord,
            allowedStatusCodes:
              values.allowedStatusCodes.length > 0
                ? values.allowedStatusCodes.split(',').map((code) => code.trim())
                : [],
            timeOut: values.timeOut,
            slowResponseAlertStatus: values.slowResponseAlertStatus,
            slowResponseAlertValue: values.slowResponseAlertValue,
            sslCertControl: values.sslCertControl,
            sslCertExpire: values.sslCertExpire,
            domainExpire: values.domainExpire,
          }
          url = `monitors/keyword/${params.id}`
          break
        }
        case 'cronjob': {
          formattedData.cronJobMonitor = {
            deviationTime: Number(values.deviationTime),
          }
          url = `monitors/cronjob/${params.id}`
          break
        }
        default: {
          break
        }
      }
      console.log(formattedData)
      const response = await api.patch(url, formattedData)
      if (response.data) {
        Swal.fire({
          title: 'İzleme Başarılı Şekilde Güncellendi',
          icon: 'success',
          confirmButtonText: 'Tamam',
          confirmButtonColor: '#10b981',
          background: alpha('#0f172a', 0.98),
          color: '#fff',
        })
        turnMonitorPage()
      }
    } catch (error) {
      Swal.fire({
        title: error.response.data.message,
        icon: 'error',
        confirmButtonText: 'Tamam',
        confirmButtonColor: '#10b981',
        background: alpha('#0f172a', 0.98),
        color: '#fff',
      })
      console.error('Monitor update error :', error)
    }
  }

  const handleMonitorTypeChange = (event) => {
    const newType = event.target.value
    setMonitorType(newType)
    if (newType === 'http') {
      setFieldValue('method', 'HEAD')
    } else if (newType === 'keyword') {
      setFieldValue('method', 'GET')
    }
  }

  const turnMonitorPage = () => {
    role === 'user' ? navigate('/user/monitors/') : navigate(`/admin/userMonitors/${params.userId}`)
  }

  const { values, errors, isValid, handleChange, handleSubmit, setFieldValue } = useFormik(getFormikConfig())

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      setValidateOnChangeState(true)
      setValidateOnBlurState(false)
    }
    console.log('Errors : ', errors)
  }, [errors])

  useEffect(() => {
    console.log('yeni cron ', values.cronSchedule)
  }, [values.cronSchedule])

  // TextField Styles
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      bgcolor: alpha('#fff', 0.05),
      '& fieldset': {
        borderColor: alpha('#10b981', 0.3),
      },
      '&:hover fieldset': {
        borderColor: alpha('#10b981', 0.5),
      },
      '&.Mui-focused fieldset': {
        borderColor: '#10b981',
      },
    },
    '& .MuiInputLabel-root': {
      color: alpha('#fff', 0.7),
    },
  }

  // PART 1 ENDS HERE - CONTINUES IN PART 2
  // return null
  return !isLoading && (
  <Box sx={{ width: '100%', p: 3 }}>
    {/* Header */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        mb: 3,
        gap: 2,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          background: `linear-gradient(135deg, #10b981, #34d399)`,
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontSize: { xs: '1.5rem', md: '2rem' },
        }}
      >
        {update.update ? 'Monitor Güncelle' : 'Yeni Monitor Ekle'}
      </Typography>
    </Box>

    <Divider sx={{ mb: 4, borderColor: alpha('#10b981', 0.2) }} />

    {/* Alert Info */}
    {alertContent && (
      <Alert
        severity="info"
        sx={{
          mb: 3,
          bgcolor: alpha('#10b981', 0.1),
          border: `1px solid ${alpha('#10b981', 0.3)}`,
          '& .MuiAlert-icon': {
            color: '#10b981',
          },
        }}
      >
        {alertContent}
      </Alert>
    )}

    {/* Monitor Type Selection */}
    <Card
      sx={{
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha('#10b981', 0.2)}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: '#fff',
          mb: 2,
        }}
      >
        Monitoring Tipi
      </Typography>

      <FormControl fullWidth>
        <Select
          value={monitorType}
          onChange={handleMonitorTypeChange}
          displayEmpty
          sx={{
            ...textFieldStyles,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              py: 1.5,
            },
          }}
        >
          {monitorTypes.map((item) => (
            <MenuItem
              key={item.id}
              value={item.id}
              disabled={!item.access || update.update}
              sx={{
                opacity: !item.access ? 0.6 : 1,
                cursor: !item.access ? 'not-allowed' : 'pointer',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    bgcolor: item.bgcolor,
                    p: 1,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 48,
                  }}
                >
                  {item.icon}
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.info}
                  </Typography>
                </Box>

                {!item.access && (
                  <Chip
                    label="🔒 Paket Yükseltme"
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: alpha('#f59e0b', 0.1),
                      color: '#f59e0b',
                      border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Card>

    {/* Basic Info */}
    <Card
      sx={{
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha('#10b981', 0.2)}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: '#fff',
          mb: 3,
        }}
      >
        Temel Bilgiler
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>Ad</Typography>
          <TextField
            id="name"
            required
            fullWidth
            placeholder="Tanımlayıcı ad"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            sx={textFieldStyles}
          />
        </Grid>

        {monitorType === 'cronjob' ? (
          <Grid item xs={12} md={6}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
              Sapma Zamanı (Dakika)
            </Typography>
            <TextField
              id="deviationTime"
              name="deviationTime"
              required
              fullWidth
              placeholder="Sapma zamanı dakika cinsinden"
              value={values.deviationTime}
              onChange={handleChange}
              error={Boolean(errors.deviationTime)}
              helperText={errors.deviationTime}
              sx={textFieldStyles}
            />
          </Grid>
        ) : (
          <Grid item xs={12} md={6}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>Host</Typography>
            <TextField
              id="host"
              required
              fullWidth
              placeholder="URL veya IP adresi"
              value={values.host}
              onChange={handleChange}
              error={Boolean(errors.host)}
              helperText={errors.host}
              sx={textFieldStyles}
            />
          </Grid>
        )}
      </Grid>
    </Card>

    {/* Cron Schedule */}
    <Card
      sx={{
        bgcolor: alpha('#fff', 0.03),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha('#10b981', 0.2)}`,
        borderRadius: 2,
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: '#fff',
          mb: 2,
        }}
      >
        Zamanlama
      </Typography>
      <ModernCronScheduleEditor value={values.cronSchedule} setFieldValue={setFieldValue} plan={plan} />
    </Card>

    {/* HTTP Methods */}
    {(monitorType === 'http' || monitorType === 'keyword') && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#fff',
            mb: 2,
          }}
        >
          HTTP Metod
        </Typography>

        <Grid container spacing={2}>
          {HTTP_METHODS.filter((method) => !(monitorType === 'keyword' && method.value === 'HEAD')).map(
            (method) => {
              const isSelected = values.method === method.value
              const isDisabled = !method.access

              return (
                <Grid item xs={6} sm={4} md={2} key={method.value}>
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Button
                      variant={isSelected ? 'contained' : 'outlined'}
                      size="medium"
                      disabled={isDisabled}
                      onClick={() => !isDisabled && setFieldValue('method', method.value)}
                      fullWidth
                      sx={{
                        height: 48,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        textTransform: 'none',
                        borderWidth: 2,
                        ...(isSelected && {
                          bgcolor: '#10b981',
                          borderColor: '#10b981',
                          color: '#000',
                          '&:hover': {
                            bgcolor: '#059669',
                            borderColor: '#059669',
                          },
                        }),
                        ...(!isSelected &&
                          !isDisabled && {
                            borderColor: alpha('#10b981', 0.3),
                            color: '#10b981',
                            '&:hover': {
                              borderColor: '#10b981',
                              bgcolor: alpha('#10b981', 0.1),
                            },
                          }),
                        ...(isDisabled && {
                          opacity: 0.4,
                          borderColor: alpha('#fff', 0.1),
                        }),
                      }}
                      endIcon={
                        isDisabled ? (
                          <LockIcon sx={{ fontSize: 16 }} />
                        ) : isSelected ? (
                          <CheckIcon sx={{ fontSize: 16 }} />
                        ) : null
                      }
                    >
                      {method.label}
                    </Button>

                    {isDisabled && (
                      <Chip
                        label="Pro"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          height: 20,
                          fontSize: '0.65rem',
                          fontWeight: 700,
                          bgcolor: alpha('#f59e0b', 0.15),
                          color: '#f59e0b',
                          border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                        }}
                      />
                    )}
                  </Box>
                </Grid>
              )
            }
          )}
        </Grid>

        {errors.method && (
          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 2 }}>
            {errors.method}
          </Typography>
        )}
      </Card>
    )}

    {/* Fail Count for ping/port/cronjob */}
    {(monitorType === 'ping' || monitorType === 'port' || monitorType === 'cronjob') && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#fff',
            mb: 2,
          }}
        >
          Hata Eşiği
        </Typography>

        <Typography sx={{ color: alpha('#fff', 0.6), mb: 2 }}>
          Kaç hata sonrası bildirim gönderilsin?
        </Typography>

        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={handleDecrementForFailCount}
            disabled={values.failCountRef <= 1}
            sx={{
              width: 48,
              height: 48,
              border: `2px solid ${alpha('#10b981', 0.3)}`,
              borderRadius: 2,
              bgcolor: alpha('#10b981', 0.05),
              '&:hover': {
                bgcolor: alpha('#10b981', 0.15),
                borderColor: '#10b981',
              },
              '&:disabled': {
                opacity: 0.3,
              },
            }}
          >
            <Remove sx={{ color: '#10b981' }} />
          </IconButton>

          <TextField
            id="failCountRef"
            name="failCountRef"
            value={values.failCountRef}
            onChange={handleChange}
            variant="outlined"
            sx={{
              flex: 1,
              ...textFieldStyles,
              '& input': {
                textAlign: 'center',
                fontSize: '1.2rem',
                fontWeight: 700,
                color: '#10b981',
              },
            }}
            inputProps={{
              type: 'number',
              min: 1,
            }}
          />

          <IconButton
            onClick={handleIncrementForFailCount}
            sx={{
              width: 48,
              height: 48,
              border: `2px solid ${alpha('#10b981', 0.3)}`,
              borderRadius: 2,
              bgcolor: alpha('#10b981', 0.05),
              '&:hover': {
                bgcolor: alpha('#10b981', 0.15),
                borderColor: '#10b981',
              },
            }}
          >
            <Add sx={{ color: '#10b981' }} />
          </IconButton>
        </Stack>

        {errors.failCountRef && (
          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 1 }}>
            {errors.failCountRef}
          </Typography>
        )}
      </Card>
    )}

    {/* Request Details Accordion */}
    {(monitorType === 'http' || monitorType === 'keyword') && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <Accordion
          sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#10b981' }} />}
            sx={{
              px: 3,
              '&:hover': {
                bgcolor: alpha('#10b981', 0.05),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', flex: 1 }}>
                Gönderilecek İstek Detayı
              </Typography>
              {!plan.features.advance_request && (
                <Chip
                  label="🔒 Paket Yükseltme"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: alpha('#f59e0b', 0.1),
                    color: '#f59e0b',
                    border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
                  Başlık (JSON)
                </Typography>
                <TextField
                  id="headers"
                  name="headers"
                  fullWidth
                  multiline
                  rows={7}
                  disabled={!plan.features.advance_request}
                  value={values.headers}
                  onChange={handleChange}
                  error={Boolean(errors.headers)}
                  helperText={errors.headers}
                  sx={{
                    ...textFieldStyles,
                    opacity: !plan.features.advance_request ? 0.5 : 1,
                  }}
                  InputProps={{
                    startAdornment: <CodeIcon sx={{ mr: 1, color: '#10b981' }} />,
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
                  Gövde (JSON)
                </Typography>
                <TextField
                  id="body"
                  name="body"
                  fullWidth
                  multiline
                  rows={7}
                  disabled={!plan.features.advance_request}
                  value={values.body}
                  onChange={(e) => setFieldValue('body', e.target.value)}
                  error={Boolean(errors.body)}
                  helperText={errors.body}
                  sx={{
                    ...textFieldStyles,
                    opacity: !plan.features.advance_request ? 0.5 : 1,
                  }}
                  InputProps={{
                    startAdornment: <CodeIcon sx={{ mr: 1, color: '#10b981' }} />,
                  }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Card>
    )}

    {/* Keyword Specific */}
    {monitorType === 'keyword' && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#fff',
            mb: 3,
          }}
        >
          Anahtar Kelime Ayarları
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
              Anahtar Kelime
            </Typography>
            <TextField
              id="keyWord"
              name="keyWord"
              fullWidth
              multiline
              rows={3}
              value={values.keyWord}
              onChange={(e) => setFieldValue('keyWord', e.target.value)}
              error={Boolean(errors.keyWord)}
              helperText={errors.keyWord}
              sx={textFieldStyles}
              InputProps={{
                startAdornment: <CodeIcon sx={{ mr: 1, color: '#10b981' }} />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
              Anahtar Kelime Tipi
            </Typography>
            <TextField
              id="keyWordType"
              name="keyWordType"
              fullWidth
              select
              value={values.keyWordType}
              onChange={(e) => setFieldValue('keyWordType', e.target.value)}
              error={Boolean(errors.keyWordType)}
              helperText={errors.keyWordType}
              sx={textFieldStyles}
            >
              <MenuItem value="txt">TXT</MenuItem>
              <MenuItem value="html">HTML</MenuItem>
              <MenuItem value="json">JSON</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </Card>
    )}

    {/* Timeout & Status Codes & Port */}
    {(monitorType === 'http' || monitorType === 'port' || monitorType === 'keyword') && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: '#fff',
            mb: 3,
          }}
        >
          İstek Ayarları
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={monitorType === 'port' ? 6 : 6}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
              İstek Zaman Aşımı
            </Typography>
            <FormControl fullWidth>
              <Select
                id="timeOut"
                name="timeOut"
                value={values.timeOut}
                onChange={(e) => setFieldValue('timeOut', e.target.value)}
                sx={textFieldStyles}
              >
                <MenuItem value={10}>10 saniye</MenuItem>
                <MenuItem value={20}>20 saniye</MenuItem>
                <MenuItem value={30}>30 saniye</MenuItem>
                <MenuItem value={40}>40 saniye</MenuItem>
                <MenuItem value={50}>50 saniye</MenuItem>
                <MenuItem value={60}>60 saniye</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {(monitorType === 'http' || monitorType === 'keyword') && (
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ color: alpha('#fff', 0.8), fontWeight: 500 }}>
                  İzin Verilen Durum Kodları
                </Typography>
                {!plan.features.status_code_alerts && (
                  <Chip
                    label="🔒 Pro"
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      bgcolor: alpha('#f59e0b', 0.1),
                      color: '#f59e0b',
                    }}
                  />
                )}
              </Box>
              <TextField
                id="allowedStatusCodes"
                name="allowedStatusCodes"
                placeholder="örnek: 200,400,500"
                fullWidth
                disabled={!plan.features.status_code_alerts}
                value={values.allowedStatusCodes}
                onChange={handleChange}
                error={Boolean(errors.allowedStatusCodes)}
                helperText={errors.allowedStatusCodes}
                sx={{
                  ...textFieldStyles,
                  opacity: !plan.features.status_code_alerts ? 0.5 : 1,
                }}
              />
            </Grid>
          )}

          {monitorType === 'port' && (
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
                Port Numarası
              </Typography>
              <TextField
                id="port"
                name="port"
                fullWidth
                placeholder="örnek: 80, 3000"
                value={values.port}
                onChange={handleChange}
                error={Boolean(errors.port)}
                helperText={errors.port}
                sx={textFieldStyles}
              />
            </Grid>
          )}
        </Grid>
      </Card>
    )}

    {/* SSL & Domain Accordion */}
    {(monitorType === 'http' || monitorType === 'keyword') && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          mb: 3,
          overflow: 'hidden',
        }}
      >
        <Accordion
          sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: '#10b981' }} />}
            sx={{
              px: 3,
              '&:hover': {
                bgcolor: alpha('#10b981', 0.05),
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography sx={{ fontWeight: 700, color: '#fff', flex: 1 }}>
                SSL Sertifikası ve Domain Kontrolü
              </Typography>
              {!plan.features.ssl_monitoring && (
                <Chip
                  label="🔒 Paket Yükseltme"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: '0.75rem',
                    bgcolor: alpha('#f59e0b', 0.1),
                    color: '#f59e0b',
                    border: `1px solid ${alpha('#f59e0b', 0.3)}`,
                  }}
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 3, pb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#10b981', 0.05),
                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                  }}
                >
                  <Switch
                    disabled={!plan.features.ssl_monitoring}
                    checked={values.sslCertControl}
                    onChange={() => setFieldValue('sslCertControl', !values.sslCertControl)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#10b981',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#10b981',
                      },
                    }}
                  />
                  <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>SSL Hatalarını Kontrol Et</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#10b981', 0.05),
                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                  }}
                >
                  <Switch
                    disabled={!plan.features.ssl_monitoring}
                    checked={values.sslCertExpire}
                    onChange={() => setFieldValue('sslCertExpire', !values.sslCertExpire)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#10b981',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#10b981',
                      },
                    }}
                  />
                  <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>SSL Son Tarihinde Hatırlat</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#10b981', 0.05),
                    border: `1px solid ${alpha('#10b981', 0.2)}`,
                  }}
                >
                  <Switch
                    disabled={!plan.features.ssl_monitoring}
                    checked={values.domainExpire}
                    onChange={() => setFieldValue('domainExpire', !values.domainExpire)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#10b981',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        bgcolor: '#10b981',
                      },
                    }}
                  />
                  <Typography sx={{ color: '#fff', fontSize: '0.9rem' }}>Domain Son Tarihinde Hatırlat</Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Card>
    )}

    {/* Slow Response Alert */}
    {monitorType !== 'cronjob' && (
      <Card
        sx={{
          bgcolor: alpha('#fff', 0.03),
          backdropFilter: 'blur(20px)',
          border: `1px solid ${alpha('#10b981', 0.2)}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontWeight: 700, color: '#fff' }}>Yavaş Yanıt Bildirimi</Typography>
            {!plan.features.slow_response_alerts && (
              <Chip
                label="🔒 Pro"
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  bgcolor: alpha('#f59e0b', 0.1),
                  color: '#f59e0b',
                }}
              />
            )}
          </Box>
          <Switch
            disabled={!plan.features.slow_response_alerts}
            checked={values.slowResponseAlertStatus}
            onChange={() => setFieldValue('slowResponseAlertStatus', !values.slowResponseAlertStatus)}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#10b981',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                bgcolor: '#10b981',
              },
            }}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={(monitorType === 'http' || monitorType === 'keyword') ? 6 : 12}>
            <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
              Yanıt Süresi Eşiği (milisaniye)
            </Typography>
            <TextField
              id="slowResponseAlertValue"
              name="slowResponseAlertValue"
              fullWidth
              disabled={!values.slowResponseAlertStatus}
              value={values.slowResponseAlertValue}
              onChange={handleChange}
              error={Boolean(errors.slowResponseAlertValue)}
              helperText={errors.slowResponseAlertValue}
              sx={{
                ...textFieldStyles,
                opacity: !values.slowResponseAlertStatus ? 0.5 : 1,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography sx={{ color: alpha('#fff', 0.6) }}>ms</Typography>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {(monitorType === 'http' || monitorType === 'keyword') && (
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: alpha('#fff', 0.8), mb: 1, fontWeight: 500 }}>
                Kaç Hata Sonrası Bildirim Gönderilsin?
              </Typography>
              <Stack direction="row" alignItems="center" spacing={2}>
                <IconButton
                  onClick={handleDecrementForFailCount}
                  disabled={values.failCountRef <= 1}
                  sx={{
                    width: 48,
                    height: 48,
                    border: `2px solid ${alpha('#10b981', 0.3)}`,
                    borderRadius: 2,
                    bgcolor: alpha('#10b981', 0.05),
                    '&:hover': {
                      bgcolor: alpha('#10b981', 0.15),
                      borderColor: '#10b981',
                    },
                    '&:disabled': {
                      opacity: 0.3,
                    },
                  }}
                >
                  <Remove sx={{ color: '#10b981' }} />
                </IconButton>

                <TextField
                  id="failCountRef"
                  name="failCountRef"
                  value={values.failCountRef}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  sx={{
                    ...textFieldStyles,
                    '& input': {
                      textAlign: 'center',
                      fontSize: '1.2rem',
                      fontWeight: 700,
                      color: '#10b981',
                    },
                  }}
                  inputProps={{
                    type: 'number',
                    min: 1,
                  }}
                />

                <IconButton
                  onClick={handleIncrementForFailCount}
                  sx={{
                    width: 48,
                    height: 48,
                    border: `2px solid ${alpha('#10b981', 0.3)}`,
                    borderRadius: 2,
                    bgcolor: alpha('#10b981', 0.05),
                    '&:hover': {
                      bgcolor: alpha('#10b981', 0.15),
                      borderColor: '#10b981',
                    },
                  }}
                >
                  <Add sx={{ color: '#10b981' }} />
                </IconButton>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Card>
    )}

    {/* Action Buttons */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-end' },
        gap: 2,
        mt: 4,
      }}
    >
      <Button
        variant="outlined"
        onClick={turnMonitorPage}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 2,
          borderColor: alpha('#10b981', 0.3),
          color: '#10b981',
          fontWeight: 600,
          '&:hover': {
            borderColor: '#10b981',
            bgcolor: alpha('#10b981', 0.1),
          },
        }}
      >
        İptal
      </Button>
      <Button
        variant="contained"
        onClick={() => {
          if (isValid) {
            handleSubmit()
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Hata',
              text: 'Lütfen formu tekrar gözden geçirin',
              confirmButtonText: 'Tamam',
              confirmButtonColor: '#10b981',
              background: alpha('#0f172a', 0.98),
              color: '#fff',
            })
            handleSubmit()
          }
        }}
        sx={{
          px: 4,
          py: 1.5,
          borderRadius: 2,
          bgcolor: '#10b981',
          color: '#000',
          fontWeight: 700,
          fontSize: '1rem',
          '&:hover': {
            bgcolor: '#059669',
          },
        }}
      >
        {update.update ? 'Monitor Güncelle' : 'Monitor Oluştur'}
      </Button>
    </Box>
  </Box>
)
}


